import { Component, OnInit, AfterViewInit, AfterViewChecked, ElementRef, ViewChild, Input, OnChanges } from '@angular/core';
import MetisMenu from 'metismenujs';
import { EventService } from '../../core/services/event.service';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { MENU, getMenu } from './menu';
import { MenuItem } from './menu.model';
import { ProjectService } from '../../core/services/project.service';
import { SharedOperationsService } from '../../core/services/shared-operations.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: false
})

/**
 * Sidebar component
 */
export class SidebarComponent implements OnInit, AfterViewChecked, AfterViewInit, OnChanges {

  @Input() isCondensed = false;
  menu: any;

  menuItems = [];
  configData;

  projectId: string;
  storeId: string;
  hasActiveStore = false;

  @ViewChild('sideMenu') sideMenu: ElementRef;

  constructor(private eventService: EventService, private router: Router, private route: ActivatedRoute, private cookieService: CookieService, private projectService: ProjectService, private sharedOperationsService: SharedOperationsService) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._updateActiveStore();
        this._activateMenuDropdown();
      }
    });
  }

  _updateActiveStore() {
    const currentStoreId = this.sharedOperationsService.getStoreIdFromLocalStorage();
    this.hasActiveStore = !!currentStoreId;
    if (this.projectId && this.storeId !== currentStoreId) {
      this.storeId = currentStoreId;
      // trackBy keeps the menu's <li>/<ul> DOM nodes (labels/structure are unchanged,
      // only leaf links differ), so MetisMenu's existing bindings stay valid — no re-init.
      this.menuItems = getMenu(this.projectId, this.storeId);
    }
  }

  getCurrentProjectId(from: string) {
    const currentProjectId = this.route["_routerState"].snapshot['url'].split('/')[2];
    if (currentProjectId && this.projectId != currentProjectId && currentProjectId != 'create' && currentProjectId != 'select') {
      this.projectId = currentProjectId;
      this.storeId = this.sharedOperationsService.getStoreIdFromLocalStorage();
      // trackBy keeps the menu DOM nodes across this reassignment, so the MetisMenu
      // instance bound in ngAfterViewInit stays valid — no re-init needed here.
      this.menuItems = getMenu(currentProjectId, this.storeId);
    }
  }

  trackByMenuItem(index: number, item: MenuItem) {
    return item.label;
  }

  /**
   * (Re)initialize MetisMenu on the current menu DOM, disposing any previous instance
   * so stale click handlers and duplicate bindings don't pile up. Only called when the
   * menu DOM is genuinely (re)built: first render (ngAfterViewInit) and the condensed
   * toggle (ngOnChanges). Bails out if the submenu DOM isn't present yet, so it never
   * tears down a working instance and leaves an unbound one behind.
   */
  private _initMenu() {
    const el: HTMLElement = this.sideMenu?.nativeElement;
    if (!el || el.querySelectorAll('ul').length === 0) {
      return;
    }
    this.menu?.dispose();
    this.menu = new MetisMenu(el);
    this._activateMenuDropdown();
  }

  ngOnInit() {
    this.initialize();

    this.getCurrentProjectId("On Init");
    this._updateActiveStore();

    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.getCurrentProjectId("router change");
      }
    });

    document.body.setAttribute('data-sidebar', 'dark');

    this.configData = {
      suppressScrollX: true,
      wheelSpeed: 0.3
    };
  }
  /**
   * Change the layout onclick
   * @param layout Change the layout
   */
  changeLayout(layout: string) {
    this.eventService.broadcast('changeLayout', layout);
  }

  ngAfterViewInit() {
    this._initMenu();
  }

  ngAfterViewChecked(): void {
    // Safety net for the case where the router URL wasn't available yet on init:
    // this sets projectId / rebuilds the menu (and re-binds MetisMenu) once it is.
    this.getCurrentProjectId("after view checked");
  }

  ngOnChanges() {
    if (!this.isCondensed && this.sideMenu || this.isCondensed) {
      setTimeout(() => this._initMenu());
    } else if (this.menu) {
      this.menu.dispose();
    }
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
   * Light sidebar
   */
  lightSidebar() {
    document.body.setAttribute('data-sidebar', 'light');
    document.body.setAttribute('data-topbar', 'dark');
    document.body.removeAttribute('data-sidebar-size');
    document.body.removeAttribute('data-layout-size');
    document.body.removeAttribute('data-keep-enlarged');
    document.body.classList.remove('vertical-collpsed');
  }

  /**
   * Compact sidebar
   */
  compactSidebar() {
    document.body.setAttribute('data-sidebar-size', 'small');
    document.body.setAttribute('data-sidebar', 'dark');
    document.body.removeAttribute('data-topbar');
    document.body.removeAttribute('data-layout-size');
    document.body.removeAttribute('data-keep-enlarged');
    document.body.classList.remove('sidebar-enable');
    document.body.classList.remove('vertical-collpsed');
  }

  /**
   * Icon sidebar
   */
  iconSidebar() {
    document.body.classList.add('sidebar-enable');
    document.body.classList.add('vertical-collpsed');
    document.body.setAttribute('data-sidebar', 'dark');
    document.body.removeAttribute('data-layout-size');
    document.body.removeAttribute('data-keep-enlarged');
    document.body.removeAttribute('data-topbar');
  }

  /**
   * Boxed layout
   */
  boxedLayout() {
    document.body.setAttribute('data-keep-enlarged', 'true');
    document.body.setAttribute('data-layout-size', 'boxed');
    document.body.setAttribute('data-sidebar', 'dark');
    document.body.classList.add('vertical-collpsed');
    document.body.classList.remove('sidebar-enable');
    document.body.removeAttribute('data-topbar');
  }

  /**
   * Colored sidebar
   */
  coloredSidebar() {
    document.body.setAttribute('data-sidebar', 'colored');
    document.body.removeAttribute('data-sidebar-size');
    document.body.removeAttribute('data-layout-size');
    document.body.classList.remove('vertical-collpsed');
    document.body.removeAttribute('data-topbar');
  }

  /**
   * Activate the parent dropdown
   */
  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    if (!this.projectId) {
      return;
    }
    const links = document.getElementsByClassName('side-nav-link-ref');
    let menuItemEl = null;
    // tslint:disable-next-line: prefer-for-of
    const paths = [];
    for (let i = 0; i < links.length; i++) {
      paths.push(links[i]['pathname']);
    }
    var itemIndex = paths.indexOf(window.location.pathname);
    if (itemIndex === -1) {
      const strIndex = window.location.pathname.lastIndexOf('/');
      const item = window.location.pathname.substr(0, strIndex).toString();
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }
    if (menuItemEl) {
      menuItemEl.classList.add('active');
      const parentEl = menuItemEl.parentElement;
      if (parentEl) {
        parentEl.classList.add('mm-active');
        const parent2El = parentEl.parentElement.closest('ul');
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.add('mm-show');
          const parent3El = parent2El.parentElement;
          if (parent3El && parent3El.id !== 'side-menu') {
            parent3El.classList.add('mm-active');
            const childAnchor = parent3El.querySelector('.has-arrow');
            const childDropdown = parent3El.querySelector('.has-dropdown');
            if (childAnchor) { childAnchor.classList.add('mm-active'); }
            if (childDropdown) { childDropdown.classList.add('mm-active'); }
            const parent4El = parent3El.parentElement;
            if (parent4El && parent4El.id !== 'side-menu') {
              parent4El.classList.add('mm-show');
              const parent5El = parent4El.parentElement;
              if (parent5El && parent5El.id !== 'side-menu') {
                parent5El.classList.add('mm-active');
                const childanchor = parent5El.querySelector('.is-parent');
                if (childanchor && parent5El.id !== 'side-menu') { childanchor.classList.add('mm-active'); }
              }
            }
          }
        }
      }
    }

  }

  /**
   * Initialize
   */
  initialize(): void {
    this.storeId = this.sharedOperationsService.getStoreIdFromLocalStorage();
    this.menuItems = getMenu(this.projectId, this.storeId);
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

}

